import { ApiResponse } from 'Public_old/src/app/Classes/visibaHttp.class';
import { Helper } from 'Public_old/src/app/Common/Helpers/helper';
import { DateService } from 'Public_old/src/app/Services/date.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticService } from '@application/analytic/analytic.service';
import { createAppStoreDownloadLink, createPlayStoreDownloadLink } from '@helpers/app_download_link';
import { isWithinVisibaWebView } from '@public/src/modules/is_within_visiba_web_view';
import { LocationSelectorService } from '@services/location_selector.service';
import { Subscription } from 'rxjs';
import { IDP_AUTHENTICATION_PROVIDER_ID } from '../../../../../patient/src/constants';
import { FeatureFlagService } from '../../../../src/application/services/feature_flag/feature_flag.service';
import { sharedResolution } from '../../../../src/shared_resolution';
import { SpaContext } from '../../Classes/spacontext';
import { IdentityServiceV2 } from '../../Services/api/v2/identity.service';
import { AuthContextService } from '../../Services/authContext.service';
import { GeneralService } from '../../Services/general.service';
import { AppointmentAccessApiModel } from '../../Services/Models/General/AppointmentAccessApiModel';
import { AppointmentCalendarInfoApiModel } from '../../Services/Models/General/AppointmentCalendarInfoApiModel';
import { SpaEventService } from '../../Services/spaEvents.service';

enum AppointmentState {
	cancelled = 'Cancelled',
	completed = 'Completed',
	early = 'Early',
	startable = 'Startable',
	requiresSithsWeb = 'RequiresSithsWeb',
}

@Component({ standalone: false, templateUrl: '../../ViewHtml/Guest/Guest.html', styleUrls: ['../../Css/Views/Guest/Guest.scss'] })
export class GuestComponent {
	public state: AppointmentState | null = null;
	public AppointmentState = AppointmentState;
	public applink = {
		ios: null,
		android: null,
	};
	public isWithinWebView = false;
	public callTicket: Guid;
	public unitUrl = this.helper.unit.getFullUrlAlias(this.spaContext.unitConfig.Unit.UnitId, this.spaContext.unitTreeFlat);
	public showOverlay = false;
	public get allowWebRedirect(): boolean {
		return !this.isWithinWebView;
	}
	public readonly device: 'Desktop' | 'Android' | 'iOS' = 'Desktop';
	private lobbyOpenCheckInterval = null;
	private subscribers: Subscription[] = [];
	private accessInfo: AppointmentAccessApiModel = null;
	public calendarInfo: AppointmentCalendarInfoApiModel = null;
	public guestLink: string | null = null;

	public readonly locationSelectorService = sharedResolution.resolve(LocationSelectorService);
	private readonly analyticService = sharedResolution.resolve(AnalyticService);

	constructor(
		public spaContext: SpaContext,
		public route: ActivatedRoute,
		public router: Router,
		public authContextService: AuthContextService,
		private generalService: GeneralService,
		private identityService: IdentityServiceV2,
		private spaEventService: SpaEventService,
		private helper: Helper,
		private dateService: DateService,
	) {
		if (isWithinVisibaWebView()) {
			this.isWithinWebView = true;
		}

		if (this.helper.is.ios() || this.isVisibaApp()) {
			this.device = 'iOS';

			if (this.spaContext.unitConfig.AppConfig !== null && this.spaContext.unitConfig.AppConfig.AppStoreId !== null) {
				this.applink.ios = createAppStoreDownloadLink(this.spaContext.unitConfig.AppConfig.AppStoreId, this.spaContext.languageCode);
			}
		} else if (platform.os.family === 'Android') {
			this.device = 'Android';

			if (this.spaContext.unitConfig.AppConfig !== null && this.spaContext.unitConfig.AppConfig.AndroidPackageId !== null) {
				this.applink.android = createPlayStoreDownloadLink(this.spaContext.unitConfig.AppConfig.AndroidPackageId);
			}
		}
	}

	ngOnInit() {
		this.analyticService.trackPageView({ customTitle: 'Appointment guest' });

		this.callTicket = this.route.snapshot.paramMap.get('callTicket');

		this.generalService.getAppointmentCalendarInfo(this.callTicket).subscribe((response) => {
			this.calendarInfo = response.data;
			this.handleReceivedDataChange();
		});

		this.generalService.getAppointmentAccessInfo(this.callTicket).subscribe(
			(response) => {
				this.accessInfo = response.data;
				if (this.accessInfo.AuthenticationMethod.AuthenticationProviderId === IDP_AUTHENTICATION_PROVIDER_ID) {
					window.location.href = `${this.locationSelectorService.entrance.route}/guest-participant/new/${this.callTicket}`;

					return;
				}

				this.handleReceivedDataChange();
			},
			(err: ApiResponse<HttpErrorResponse>) => {
				if (err.statusCode === 404) {
					this.state = AppointmentState.cancelled;
				}
			},
		);

		this.guestLink = `/${this.locationSelectorService.entrance.route}/appointment/${this.callTicket}?legacy=true`;
	}

	private handleReceivedDataChange() {
		if (this.accessInfo && (this.accessInfo?.OutsiderId === null || this.accessInfo?.AuthenticationMethod === null)) {
			const locationSelectorService = sharedResolution.resolve(LocationSelectorService);
			this.router.navigateByUrl('/' + locationSelectorService.location.route);
		} else if (this.calendarInfo?.Completed || this.isAfterEndTime()) {
			this.state = AppointmentState.completed;
		} else if (this.calendarInfo?.Cancelled) {
			this.state = AppointmentState.cancelled;
		} else if (
			this.allowWebRedirect &&
			this.accessInfo &&
			this.accessInfo?.AuthenticationMethod &&
			this.accessInfo?.AuthenticationMethod.AuthenticationProviderId === 'Siths'
		) {
			this.state = AppointmentState.requiresSithsWeb;
		} else if (this.isAfterStartTime()) {
			this.state = AppointmentState.startable;
		} else {
			this.state = AppointmentState.early;
			this.lobbyOpenCheckInterval = setInterval(() => this.checkLobbyStatus(), 1000);
		}
	}

	ngOnDestroy() {
		this.subscribers.forEach((s) => s.unsubscribe());
		if (this.lobbyOpenCheckInterval) {
			clearInterval(this.lobbyOpenCheckInterval);
		}
	}

	public handleRouteClick() {
		this.showOverlay = true;
		if (this.device === 'iOS' && !this.isVisibaApp()) {
			window.open(this.applink.ios, '_blank');

			return;
		}

		window.open(`${this.unitUrl}/appointment/guest/${this.callTicket}?legacy=true`, '_blank');
	}

	// https://github.com/angular/angular/issues/28917#issuecomment-606417549
	public handleRouteClickWeb(e: MouseEvent) {
		e.preventDefault();

		this.router.navigateByUrl(this.guestLink);
	}

	public isOpen() {
		return (
			this.state === AppointmentState.early ||
			this.state === AppointmentState.startable ||
			this.state === AppointmentState.requiresSithsWeb
		);
	}

	public signOut() {
		this.identityService.signOut().subscribe(
			({ data }) => {
				this.spaEventService.onSignedOut.emit({
					redirectUrl: data.RedirectUrl,
				});
			},
			(err) => {
				window.location.reload();
			},
		);
	}

	private isVisibaApp(): boolean {
		// Exist to detect if the user is coming from an iPad
		try {
			const cookie = this.helper.cookie.get('VisibaIOSApp');

			if (!cookie) return false;

			return JSON.parse(cookie);
		} catch (_error) {
			return false;
		}
	}

	private checkLobbyStatus() {
		if (this.isAfterStartTime()) {
			this.state = AppointmentState.startable;
			clearInterval(this.lobbyOpenCheckInterval);
		}
	}

	/**
	 * Lobby is open if within 15minutes from start
	 * */
	private isAfterStartTime(): boolean {
		return new Date(this.calendarInfo.AppointmentStart).getTime() - 15 * 60 * 1000 < new Date().getTime();
	}

	private isAfterEndTime(): boolean {
		return this.dateService.create().isAfter(this.dateService.create(this.calendarInfo.AppointmentEnd), 'day');
	}
}
